/**
 * MonthlyBalanceChart - Recharts LineChart showing deposit/bank balance over 12 months.
 *
 * When monthlyResults are available (from prosumer billing engine), shows actual
 * deposit balance (net-billing) or bank balance (net-metering).
 * Falls back to cumulative savings approximation when detailed data is missing.
 */

import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Text, Paper } from '@mantine/core';
import type { ProsumerTariffComparison } from '../../types';

export interface MonthlyBalanceChartProps {
  comparisons: ProsumerTariffComparison[];
}

const MONTH_LABELS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

const COLORS = ['#228be6', '#40c057', '#fab005', '#fa5252', '#7950f2'];

function hasMonthlyResults(comparisons: ProsumerTariffComparison[]): boolean {
  return comparisons.some(c => c.prosumerResult?.monthlyResults && c.prosumerResult.monthlyResults.length === 12);
}

function buildBalanceData(comparisons: ProsumerTariffComparison[]) {
  return MONTH_LABELS.map((month, idx) => {
    const entry: Record<string, any> = { month };
    for (const comp of comparisons) {
      const mr = comp.prosumerResult?.monthlyResults?.[idx];
      if (mr) {
        // Net-billing → deposit balance; net-metering → bank balance (kWh)
        entry[comp.tariffType] = Math.round(mr.depositBalancePln ?? mr.bankBalanceKwh ?? 0);
      }
    }
    return entry;
  });
}

function buildSavingsData(comparisons: ProsumerTariffComparison[]) {
  return MONTH_LABELS.map((month, idx) => {
    const entry: Record<string, any> = { month };
    for (const comp of comparisons) {
      const monthlySaving = comp.savingsPln / 12;
      entry[comp.tariffType] = Math.round(monthlySaving * (idx + 1));
    }
    return entry;
  });
}

export function MonthlyBalanceChart({ comparisons }: MonthlyBalanceChartProps) {
  const hasDetailed = hasMonthlyResults(comparisons);
  const data = hasDetailed ? buildBalanceData(comparisons) : buildSavingsData(comparisons);
  const yLabel = hasDetailed ? 'PLN / kWh' : 'PLN';
  const title = hasDetailed
    ? 'Saldo depozytu / magazynu energii (miesiąc po miesiącu)'
    : 'Skumulowane oszczędności (miesiąc po miesiącu)';

  return (
    <Paper withBorder p="md" radius="md">
      <Text fw={600} mb="sm">{title}</Text>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis label={{ value: yLabel, angle: -90, position: 'insideLeft' }} />
          <Tooltip />
          <Legend />
          {comparisons.map((comp, idx) => (
            <Line
              key={comp.tariffType}
              type="monotone"
              dataKey={comp.tariffType}
              name={comp.tariffType}
              stroke={COLORS[idx % COLORS.length]}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
